import type { ProbeSignals } from '@fraudeai/schema';
import { defineModule, type ModuleContext } from '../harness.js';
import { attempt, attemptAsync, round, withTimeout } from '../util/safe.js';

/**
 * Внешние пробы: UDP, QUIC и DNS.
 *
 * Модуль отличается от остальных тем, что почти ничего не измеряет сам.
 * Его задача — заставить браузер обратиться к нашим службам разными
 * транспортами; выводы делает сервер, сравнивая адреса, с которых эти
 * обращения пришли.
 *
 * Так и должно быть. Смысл проверки в том, чтобы не верить странице: если бы
 * адрес по UDP сообщал сам браузер, спуферу достаточно было бы прислать
 * нужное число. Поэтому здесь фиксируется только факт «проба запущена»,
 * а результат приезжает с другой стороны.
 */

interface ProbeSession {
  token: string;
  expiresAt: number;
  endpoints: {
    stun?: string;
    http3?: string;
    dnsZone?: string;
  };
  hostname?: string;
}

export interface ParsedIceCandidate {
  address: string;
  type: string;
}

/** Разбирает только адрес и тип ICE-кандидата, нужные сетевой пробе. */
export function parseIceCandidate(line: string): ParsedIceCandidate | null {
  const match = line.match(/candidate:\S+ \d+ \S+ \d+ (\S+) \d+ typ (\S+)/i);
  if (!match?.[1] || !match[2]) return null;
  return { address: match[1], type: match[2].toLowerCase() };
}

const EMPTY: ProbeSignals = {
  requested: false,
  token: null,
  attempted: [],
  failed: [],
  localCandidates: null,
  reflexiveCandidate: null,
  mdnsObfuscated: null,
  rtt: null,
};

/**
 * Запускает WebRTC против нашего сервера.
 *
 * Токен передаётся через учётные данные TURN: в обычных запросах STUN
 * браузер не отправляет USERNAME вовсе, и связать пробу с сессией было бы
 * нечем. Ретрансляция при этом не запрашивается — сервер отвечает отказом
 * сразу после того, как прочитает имя пользователя.
 */
async function runWebrtc(
  session: ProbeSession,
): Promise<{ local: string[]; reflexive: string | null; mdns: boolean; udpMs: number | null }> {
  const Ctor = (globalThis as { RTCPeerConnection?: typeof RTCPeerConnection }).RTCPeerConnection;
  const stun = session.endpoints.stun;
  if (!Ctor || !stun) throw new Error('WebRTC или адрес STUN недоступны');

  const pc = new Ctor({
    iceServers: [
      { urls: `stun:${stun}` },
      {
        urls: `turn:${stun}?transport=udp`,
        username: session.token,
        credential: 'fraudeai',
      },
    ],
    iceCandidatePoolSize: 0,
  });

  try {
    pc.createDataChannel('fraudeai');
    const local: string[] = [];
    let reflexive: string | null = null;
    let mdns = false;
    let udpMs: number | null = null;
    let startedAt = 0;

    const gathering = new Promise<void>((resolve) => {
      pc.addEventListener('icecandidate', (event) => {
        if (!event.candidate?.candidate) return resolve();
        const candidate = parseIceCandidate(event.candidate.candidate);
        if (!candidate) return;
        const { address, type } = candidate;

        if (/\.local$/i.test(address)) {
          mdns = true;
        } else if (type === 'srflx' && !reflexive) {
          // Момент прихода отражённого кандидата — это ответ нашего STUN,
          // дошедший до браузера. Внутрь входит и работа самого браузера
          // по сбору кандидатов, поэтому величина завышена; сравнивать её
          // с замером по TCP можно только по порядку, не впритык.
          udpMs = round(performance.now() - startedAt, 1);
          // Отражённый адрес — то, каким клиента видит наш сервер.
          // В отчёт он идёт только как справка: доверять ему нельзя,
          // сравнение делает сервер по собственному наблюдению.
          reflexive = address;
        } else if (type === 'host' && !local.includes(address)) {
          local.push(address);
        }
      });
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') resolve();
      });
    });

    const offer = await pc.createOffer();
    // Отсчёт начинается с момента, когда браузер получил задание собирать
    // кандидатов: до этого никакого сетевого обмена ещё не было.
    startedAt = performance.now();
    await pc.setLocalDescription(offer);
    await withTimeout(gathering, 4000, 'ice').catch(() => undefined);

    return { local, reflexive, mdns, udpMs };
  } finally {
    attempt(() => pc.close());
  }
}

/**
 * Разрешает уникальное имя в нашей зоне.
 *
 * Ответ не нужен и почти наверняка будет ошибкой — сертификата для этого
 * имени нет. Важно другое: чтобы браузер сходил в DNS. Запрос увидит наш
 * авторитативный сервер и запишет адрес резолвера, которым устройство
 * пользуется на самом деле.
 */
async function runDnsLookup(session: ProbeSession): Promise<void> {
  const hostname = session.hostname;
  if (!hostname) throw new Error('зона DNS не настроена');

  await withTimeout(
    fetch(`https://${hostname}/probe`, {
      mode: 'no-cors',
      cache: 'no-store',
      // Ответ не интересует: важен сам факт разрешения имени.
      signal: AbortSignal.timeout(3000),
    }).then(
      () => undefined,
      () => undefined,
    ),
    4000,
    'dns',
  ).catch(() => undefined);
}

/** Обращается к эндпоинту с поддержкой HTTP/3. */
async function runHttp3(session: ProbeSession): Promise<void> {
  const origin = session.endpoints.http3;
  if (!origin) throw new Error('эндпоинт HTTP/3 не настроен');

  await withTimeout(
    fetch(`${origin}/probe/${session.token}`, {
      mode: 'no-cors',
      cache: 'no-store',
      signal: AbortSignal.timeout(4000),
    }).then(
      () => undefined,
      () => undefined,
    ),
    5000,
    'http3',
  ).catch(() => undefined);
}

/**
 * Время оборота по TCP.
 *
 * Запрос уходит к пустому эндпоинту, который отвечает 204 без тела. Из серии
 * берётся минимум: любой замер может попасть на сборку мусора, на занятость
 * планировщика или на переоткрытие соединения, и все такие помехи умеют
 * только увеличивать время. Минимум из серии ближе всего к чистому пути
 * до сервера.
 *
 * Первый запрос выбрасывается: на нём происходит установка TCP и TLS,
 * и он измеряет рукопожатие, а не оборот.
 */
async function measureTcpRtt(base: string): Promise<{ ms: number | null; samples: number; spread: number | null }> {
  const samples: number[] = [];

  for (let i = 0; i < 6; i++) {
    const ms = await attemptAsync(async () => {
      const started = performance.now();
      const response = await fetch(`${base}/api/v1/ping?n=${i}`, {
        cache: 'no-store',
        signal: AbortSignal.timeout(3000),
      });
      // Тело пустое, но ответ надо дочитать: иначе замер закончится раньше,
      // чем данные дойдут.
      await response.arrayBuffer();
      return performance.now() - started;
    });
    if (ms === null) break;
    if (i > 0) samples.push(ms);
  }

  if (samples.length === 0) return { ms: null, samples: 0, spread: null };

  const min = Math.min(...samples);
  const max = Math.max(...samples);
  return { ms: round(min, 1), samples: samples.length, spread: round(max - min, 1) };
}

export const probesModule = defineModule({
  id: 'probes',
  phase: 1,
  timeoutMs: 12000,
  async run(ctx: ModuleContext): Promise<ProbeSignals> {
    const base = ctx.options.apiBaseUrl;
    if (!base) return EMPTY;

    const session = await attemptAsync(async () => {
      const response = await fetch(`${base}/api/v1/probe/session`, {
        cache: 'no-store',
        signal: AbortSignal.timeout(4000),
      });
      if (!response.ok) return null;
      return (await response.json()) as ProbeSession;
    });

    if (!session?.token) return EMPTY;

    const attempted: string[] = [];
    const failed: string[] = [];
    let webrtc: Awaited<ReturnType<typeof runWebrtc>> | null = null;

    // Пробы идут параллельно: каждая ждёт сетевых таймаутов, и
    // последовательный запуск растянул бы проверку на десяток секунд.
    const tasks: Promise<void>[] = [];

    if (session.endpoints.stun) {
      attempted.push('stun');
      tasks.push(
        runWebrtc(session).then(
          (result) => {
            webrtc = result;
          },
          () => {
            failed.push('stun');
          },
        ),
      );
    }
    if (session.hostname) {
      attempted.push('dns');
      tasks.push(runDnsLookup(session).catch(() => void failed.push('dns')));
    }
    if (session.endpoints.http3) {
      attempted.push('http3');
      tasks.push(runHttp3(session).catch(() => void failed.push('http3')));
    }

    // Замер по TCP идёт отдельно от остальных проб и последовательно внутри
    // себя: параллельные запросы делили бы канал и завышали бы каждый.
    let tcp: Awaited<ReturnType<typeof measureTcpRtt>> = { ms: null, samples: 0, spread: null };
    tasks.push(measureTcpRtt(base).then((result) => void (tcp = result)));

    await Promise.all(tasks);

    // Небольшая пауза: наблюдения летят в API из других процессов, и отчёт
    // отправляется сразу после сбора. Без неё проба успевала бы не всегда.
    await new Promise((resolve) => setTimeout(resolve, 400));

    const result = webrtc as Awaited<ReturnType<typeof runWebrtc>> | null;

    return {
      requested: true,
      token: session.token,
      attempted,
      failed,
      localCandidates: result ? result.local : null,
      reflexiveCandidate: result ? result.reflexive : null,
      mdnsObfuscated: result ? result.mdns : null,
      rtt: {
        tcpMs: tcp.ms,
        udpMs: result ? result.udpMs : null,
        tcpSamples: tcp.samples,
        tcpSpreadMs: tcp.spread,
      },
    };
  },
});
